/**
 * Who raised a finding, and therefore who may rewrite it.
 *
 * An unattended agent may revise or withdraw what an agent raised; a person's
 * finding is theirs and the inspector leaves it exactly as they wrote it. The
 * domain API enforces that (`requireAgentMayTouch`) and the web has to predict
 * it, because a "Re-check" button offered on a finding the API will refuse is a
 * button that can only fail. Two copies of the rule drifted apart the first time
 * it changed, so there is one.
 *
 * `monitor` counts as an agent's. A monitor finding is what the sweep concluded
 * about a publication — the inspector's own reading, recorded under the run that
 * produced it rather than under anyone's name. `manual` is the one source an
 * agent may not touch, which is the whole purpose of the rule.
 *
 * Unrecognised labels read as a person's. A source nobody anticipated is not a
 * licence for an agent to rewrite the row.
 *
 * @module findingOwnership
 */

/** Sources an agent raised, as the API labels them (`inspector` is historical). */
const AGENT_RAISED: ReadonlySet<string> = new Set(["agent", "inspector", "monitor"]);

/** Whether an unattended agent may revise or withdraw a record from this source. */
export const agentMayReviseSource = (source: string | null | undefined): boolean =>
  typeof source === "string" && AGENT_RAISED.has(source);
